/**
 * skillsUtils.js — shared utilities for the SkillsSection editor.
 *
 * DATA MODEL:
 *   resume.skills: string[]              ← flat list, always kept in sync (for ATS renderers)
 *   resume.skillCategories: [{           ← user-defined categories (null/[] when not set up)
 *     id: string,
 *     name: string,
 *     skills: string[],
 *   }]
 *
 * UI MODEL:
 *   When skillCategories is empty/null → show flat skills editor (no categories)
 *   When skillCategories has entries  → show category cards
 *
 * IMPORTANT: We do NOT wrap flat skills into a fake "Skills" category.
 * Doing so would save that fake category back to the resume and corrupt the data.
 */

/** Suggested category names shown to the user when adding a new category. */
export const SUGGESTED_CATEGORIES = [
  'Languages', 'Frameworks', 'Backend', 'Frontend', 'Databases',
  'Cloud & DevOps', 'Tools & Software', 'Mobile', 'Machine Learning',
  'Professional Skills', 'Other',
];

/**
 * normaliseCategories — returns the canonical category array for the UI.
 *
 * - If skillCategories is already populated → use it (user-defined)
 * - Else → return null (signals "flat mode" — no categories set up yet)
 */
export function normaliseCategories(rawSkills, rawCategories) {
  if (Array.isArray(rawCategories) && rawCategories.length > 0) {
    return rawCategories.map((cat, i) => ({
      id: cat.id || `cat_${i}`,
      name: cat.name || 'Skills',
      skills: Array.isArray(cat.skills) ? cat.skills : [],
    }));
  }
  // No user-defined categories yet
  return null;
}

/** Generate a unique category id. */
export function uid() {
  return `cat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}
